/**
 * AI CONTEXT
 * Purpose: prepares user table rows with disabled-user filtering, search, sort.
 * Uses: persisted settings for search/result count/show-disabled/sort.
 * Data source: all users (Jotai-backed user fetch hooks).
 * Used by: UsersTable.
 * Refactor: separate selectedUser lookup from table filtering for cleaner API.
 */
import { orderBy } from "lodash"
import { useMemo } from "react"

import { User } from "#classes/User"
import { USER_SETTINGS } from "#constants/globalConstants"
import { USER_SORTABLE_FIELDS } from "#constants/userOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { type UserForTable } from "#customTypes/composedTypes/userTypes"
import { SETTINGS_KEYS, type Uuid } from "#customTypes/globalTypes"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"

import { useTableSort } from "./useTableSort"

const USER_TABLE_FILTERS = ["name", "email", "role"]

const useUserTableData = (selectedUserId: Uuid | undefined) => {
  const { getUserSetting, setUserSetting } = useUserContext()
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.USERS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.USERS_TABLE_SEARCH_TERM
  )
  const showDisabledUsers = getUserSetting<boolean>(
    USER_SETTINGS.USERS_TABLE_SHOW_DISABLED_USERS
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<UserForTable>({
      tableKey: SETTINGS_KEYS.USERS,
      sortableFields: USER_SORTABLE_FIELDS,
    })

  const setShowDisabledUsers = (showDisabledUsers: boolean) => {
    setUserSetting(
      USER_SETTINGS.USERS_TABLE_SHOW_DISABLED_USERS,
      showDisabledUsers
    )
  }

  const allUsers = useGetAllUsers()
  const isReady = !!allUsers?.length

  const filteredUsersForTable = useMemo(() => {
    const users = allUsers
      ?.filter((user) => showDisabledUsers || user.role !== USER_ROLES.DISABLED)
      .map((user) => ({
        ...new User(user).getUserForTable(),
        lastUpdatedAt: user.lastUpdatedAt,
      }))

    return orderBy(users, "name", "asc")
  }, [allUsers, showDisabledUsers])

  const sortedUsersForTable = useMemo(
    () => sortRows(filteredUsersForTable),
    [sortRows, filteredUsersForTable]
  )

  const selectedUser = useMemo(() => {
    return allUsers?.find((user) => user.id === selectedUserId)
  }, [allUsers, selectedUserId])

  const { filteredItems: filteredUsers, onSearch } = useOptimizedSearch(
    sortedUsersForTable,
    USER_TABLE_FILTERS,
    resultsCount,
    defaultSearchTerm
  )

  const usersForTable = useMemo(
    () => sortRows(filteredUsers),
    [sortRows, filteredUsers]
  )

  return {
    usersForTable,
    onSearch,
    selectedUser,
    showDisabledUsers,
    setShowDisabledUsers,
    isReady,
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useUserTableData
